import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ChatWindow from '../components/ChatWindow';
import TopBar from '../components/TopBar';
import ConversationHistory from '../components/ConversationHistory';
import { useOasisBackend } from '../../hooks/useOasisBackend';
import type { ChatMessage } from '../../api/OasisBackendApi';
import { useState, useEffect, useRef } from 'react';
import NewChatButton from '../components/buttons/NewChatButton';
import { History, X } from 'lucide-react';

const ChatPage: React.FC = () => {
  const navigate = useNavigate();
  const { conversationId: conversationIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMessage = searchParams.get('message') || undefined;
  const db = useOasisBackend();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(conversationIdParam ? parseInt(conversationIdParam) : null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [currentAgent, setCurrentAgent] = useState("multi_domain");
  const [currentModel, setCurrentModel] = useState('gpt-4.1-mini');
  const [agentParams, setAgentParams] = useState<{
    domain_id: string[];
    available_domains?: { id: string; name: string }[];
    deep_research?: boolean;
  }>({ domain_id: [] });
  const sidebarTimeoutRef = useRef<NodeJS.Timeout | null>(null);


  useEffect(() => {
    const loadConversation = async () => {
      if (conversationIdParam && conversationIdParam !== 'new') {
        const id = parseInt(conversationIdParam);
        setConversationId(id);
        
        const renderContent = (content: any) => {
          if (typeof content === 'string') {
            try {
              let parsedContent = JSON.parse(content);
              if (parsedContent.data && parsedContent.data.results) {
                parsedContent.data.results = parsedContent.data.results.map((result: any) => {
                  if (result.data && typeof result.data === 'string') {
                    return {
                      ...result,
                      data: JSON.parse(result.data)
                    }
                  }
                  return result;
                });
              }
              return parsedContent;
            } catch (error) {
              return content;
            }
          }
        }

        try {
          // Load conversation history
          const response = await db.endpoints.rhea.rheaApiConversationRetrieve(id);
          if (response.data.messages) {
            // Convert backend messages to ChatMessage format
            const chatMessages: ChatMessage[] = response.data.messages.map((msg: any) => {
              // Handle reasoning messages specially
              if (msg.role === 'reasoning') {
                try {
                  const reasoningData = typeof msg.content === 'string' 
                    ? JSON.parse(msg.content) 
                    : msg.content;
                  return {
                    role: 'reasoning',
                    content: reasoningData.text || msg.content,
                    reasoning_duration: reasoningData.duration  // Has duration = complete
                  };
                } catch {
                  return {
                    role: 'reasoning',
                    content: msg.content,
                    reasoning_duration: 0  // Fallback duration for malformed data
                  };
                }
              }
              return {
                role: msg.role,
                content: renderContent(msg.content)
              };
            });
            setMessages(chatMessages);
            
            // Set the agent from the conversation if it exists
            if (response.data.agent_slug) {
              setCurrentAgent(response.data.agent_slug);
            }

            // Set domain parameters if they exist in the conversation
            if (response.data.agent_params) {
              const params = response.data.agent_params as any;
              setAgentParams({
                domain_id: params.domain_id || '',
                available_domains: params.available_domains || [],
                deep_research: params.deep_research || false
              });
            }
          }
        } catch (error) {
          console.error('Error loading conversation:', error);
        }
      } else {
        
        // Reset state for new chat
        setConversationId(null);
      

        // Get agent parameters from URL
        const domainId = searchParams.get('domain_id');
        const availableDomainsParam = searchParams.get('available_domains');
        const deepResearchParam = searchParams.get('deep_research');
        
        // Set initial agent parameters from URL
        const initialParams: any = {};
        
        // Handle domain IDs based on agent type
        if (domainId) {
          if (currentAgent === 'multi_domain') {
            // For multi-domain, split the comma-separated list
            initialParams.domain_id = domainId.split(',');
            // Also store the full list as available_domains
            initialParams.available_domains = domainId.split(',').map((id: string) => ({ id, name: id }));
          } else {
            // For single domain, just use the ID
            initialParams.domain_id = domainId;
          }
        }

        // Handle available_domains parameter (JSON-encoded)
        if (availableDomainsParam) {
          try {
            const decodedDomains = JSON.parse(availableDomainsParam);
            if (Array.isArray(decodedDomains)) {
              initialParams.available_domains = decodedDomains;
              // If we have available_domains but no domain_id, use the first one
              if (!initialParams.domain_id && decodedDomains.length > 0) {
                initialParams.domain_id = decodedDomains[0].id;
              }
            }
          } catch (error) {
            console.error('Error parsing available_domains parameter:', error);
          }
        }

        // Handle deep_research parameter
        if (deepResearchParam !== null) {
          initialParams.deep_research = deepResearchParam === 'true';
        }

        // If we have any initial parameters, set them
        if (Object.keys(initialParams).length > 0) {
          setAgentParams(initialParams);
        }
        
        
      }
    };

    loadConversation();
  }, [conversationIdParam, currentAgent, searchParams.get('domain_id')]);

  const handleConversationSelect = (id: number) => {
    navigate(`/chat/${id}`);
  };

  const handleAgentChange = (agent: string) => {
    setCurrentAgent(agent);
  };

  const handleConversationIdChange = (id: number) => {
    window.history.replaceState({}, '', `/chat/${id}`);
  };

  const handleDomainSelect = (id: string) => {
    setAgentParams(prev => ({
      ...prev,
      domain_id: [...prev.domain_id, id]
    }));
    // Only reset if we're not in an existing conversation
    if (!conversationId) {
      setMessages([]);
      setConversationId(null);
      setResetKey(prev => prev + 1);
    }
  };

  const handleMultiDomainsSelect = (domains: { id: string; name: string }[]) => {
    if (domains.length > 0) {
      // Use the domain objects directly since they already have id and name
      const domainIds = domains.map(d => d.id);
      setAgentParams(prev => ({
        ...prev,
        available_domains: domains, // Pass the full domain objects with id and name
        domain_id: [...prev.domain_id, ...domainIds] // Use first selected domain as initial
      }));
      
      // Only reset if we're not in an existing conversation
      if (!conversationId) {
        setMessages([]);
        setConversationId(null);
        setResetKey(prev => prev + 1);
      }
    }
  };

  const handleModelChange = (model: string) => {
    setCurrentModel(model);
    // Only reset if we're in a new chat (no conversationId)
    if (!conversationId) {
      setMessages([]);
      setResetKey(prev => prev + 1);
    }
  };

  const resetSidebarTimer = () => {
    if (sidebarTimeoutRef.current) {
      clearTimeout(sidebarTimeoutRef.current);
    }
    if (isSidebarOpen) {
      sidebarTimeoutRef.current = setTimeout(() => {
        setIsSidebarOpen(false);
      }, 10000); // 10 seconds
    }
  };

  useEffect(() => {
    resetSidebarTimer();
    return () => {
      if (sidebarTimeoutRef.current) {
        clearTimeout(sidebarTimeoutRef.current);
      }
    };
  }, [isSidebarOpen]);

  const handleSidebarInteraction = () => {
    resetSidebarTimer();
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--rhea-bg-primary)' }}>
      <style>
        {`
          /* Transparent scrollbar styles */
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
            background: transparent;
          }
          
          ::-webkit-scrollbar-track {
            background: transparent;
          }
          
          ::-webkit-scrollbar-thumb {
            background: rgba(128, 128, 128, 0.2);
            border-radius: 4px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: rgba(128, 128, 128, 0.3);
          }
          
          /* Firefox scrollbar styles */
          * {
            scrollbar-width: thin;
            scrollbar-color: rgba(128, 128, 128, 0.2) transparent;
          }
        `}
      </style>
      <TopBar 
        title="Chat" 
        middleContent={
          <div className="flex items-center space-x-4">
            <NewChatButton />
          </div>
        }
      />
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden pt-[88px]">
        {/* Detection Zone */}
        <div 
          className="fixed left-0 top-[88px] bottom-0 w-[50px] z-40"
          onMouseEnter={() => {
            if (!isSidebarOpen) {
              setIsSidebarOpen(true);
              resetSidebarTimer();
            }
          }}
        />

        {/* Sidebar Toggle Button */}
        <button
          onClick={() => {
            setIsSidebarOpen(!isSidebarOpen);
            resetSidebarTimer();
          }}
          className="absolute left-0 top-[100px] z-50 p-2 rounded-r-lg bg-[var(--rhea-bg-tertiary)] hover:bg-[var(--rhea-bg-secondary)] transition-all duration-300"
          style={{ transform: isSidebarOpen ? 'translateX(320px)' : 'translateX(0)' }}
          title={isSidebarOpen ? "Hide chat history" : "Show chat history"}
        >
          {isSidebarOpen ? (
            <X size={20} className="text-[var(--rhea-text-primary)]" />
          ) : (
            <History size={20} className="text-[var(--rhea-text-primary)]" />
          )}
        </button>

        {/* Sidebar */}
        <div 
          className={`fixed left-0 top-[88px] bottom-0 w-80 border-r transition-all duration-300 ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{ borderColor: 'var(--rhea-card-border)', backgroundColor: 'var(--rhea-bg-primary)' }}
          onMouseEnter={handleSidebarInteraction}
          onMouseMove={handleSidebarInteraction}
        >
          <ConversationHistory 
            onConversationSelect={(id) => {
              handleConversationSelect(id);
              resetSidebarTimer();
            }}
            showSingleTurnConversations={false}
            searchEnabled={true}
          />
        </div>

        {/* Chat Window */}
        <div className={`flex-1 h-full overflow-hidden relative transition-all duration-300 ${isSidebarOpen ? 'ml-80' : 'ml-0'}`}>
          <div className="absolute inset-0">
            <ChatWindow
              messages={messages}
              setMessages={setMessages}
              conversationId={conversationId}
              setConversationId={setConversationId}
              resetKey={resetKey}
              isExpanded={true}
              onConversationIdChange={handleConversationIdChange}
              initialMessage={initialMessage}
              currentAgent={currentAgent}
              currentModel={currentModel}
              agentParams={agentParams}
              onModelChange={handleModelChange}
              onAgentChange={handleAgentChange}
              onAgentParamsChange={setAgentParams}
              onDomainSelect={handleDomainSelect}
              onDomainsSelect={handleMultiDomainsSelect}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;  